"use client";

import { useState } from "react";
import dynamic from "next/dynamic";
import { useSession } from "next-auth/react";
import { ofetch } from "ofetch";
import useSWRMutation from "swr/mutation";
import * as z from "zod";

import type { CreateJobApplicationApiSuccessResponse } from "@/api/apply/route";
import Button from "@/components/atoms/Button";
import { Loader } from "@/components/atoms/Loader";
import Checkbox from "@/components/forms/controls/Checkbox";
import TextInput from "@/components/forms/controls/TextInput";
import Form from "@/components/forms/Form";
import { GetCandidateQuery, GetJobQuery } from "@/graphql/sdk/__generated__";
import { createAppliedJobsCookie } from "@/helpers/cookies";
import { formatToYYYYMMDD } from "@/helpers/date";
import { generateApplyToAJobStep2Url } from "@/helpers/url";
import { getUtmSourceFromSessionStorage } from "@/helpers/utmParams";
import { useAsyncRouter } from "@/hooks/useAsyncRouter";
import { useZodForm } from "@/hooks/useZodForm";
import { useTranslation } from "@/i18n/client";
import { cn } from "@/lib/helpers/className";
import { withRetry } from "@/lib/helpers/dynamicRetryImport";
import { isValidPhoneNumber } from "@/lib/validators/isValidPhoneNumber";
import { AllowedLanguages } from "@/types/generic";

const FormInputSkeleton = () => (
  <div className="flex flex-col gap-2 lg:grid lg:grid-cols-[250px_1fr] lg:items-center lg:gap-8">
    <div className="h-5 w-24 animate-pulse rounded bg-gray-200" />
    <div className="h-12 w-full animate-pulse rounded-lg bg-gray-200" />
  </div>
);

const BirthDayInput = dynamic(
  withRetry(() => import("@/components/forms/controls/BirthDayInput")),
  { loading: FormInputSkeleton },
);
const ConfirmApplicationModal = dynamic(
  withRetry(() => import("@/components/organisms/ConfirmApplicationModal")),
);
const EmailCorrectnessConfirmationModal = dynamic(
  withRetry(() => import("@/components/organisms/EmailCorrectnessConfirmationModal")),
);
const OfficeSelector = dynamic(
  withRetry(() => import("@/components/forms/controls/OfficeSelector")),
  { loading: FormInputSkeleton },
);
const PostalCodeInput = dynamic(
  withRetry(() => import("@/components/forms/controls/PostalCodeInput")),
  { loading: FormInputSkeleton },
);

interface ApplicationFormProps {
  candidate: GetCandidateQuery["candidate"] | null | undefined;
  isInternal?: boolean;
  isStudent?: boolean;
  job?: GetJobQuery["job"];
  locale: AllowedLanguages;
  officeKey?: string;
}

export const ApplicationForm = ({
  candidate,
  locale,
  isStudent,
  isInternal,
  job,
  officeKey,
}: ApplicationFormProps) => {
  const { t } = useTranslation(locale);
  const { data: session } = useSession();
  const router = useAsyncRouter();
  const [isConfirmApplicationModalOpen, setIsConfirmApplicationModalOpen] =
    useState(true);
  const [
    isEmailCorrectnessConfirmationModalOpen,
    setIsEmailCorrectnessConfirmationModalOpen,
  ] = useState(false);

  const isSpontaneous = !job;

  const baseSchemaObject = z.object({
    first_name: z.string().min(1, t("common.error.required")),
    last_name: z.string().min(1, t("common.error.required")),
    email: z.email(t("common.error.invalid_email")).trim().toLowerCase(),
    telephone: z
      .string()
      .min(1, t("common.error.required"))
      .refine((value) => {
        // keep only numbers
        const numberOnlyValue = value.replace(/[^0-9]/g, "");
        return isValidPhoneNumber(numberOnlyValue);
      }, t("common.error.phone_format"))
      .transform((val) => {
        // keep only numbers and transform to Connexys format
        let numberOnlyValue = val.replace(/[^0-9]/g, "");

        // transform show Belgium mobile number to full: 04 xxxxxxxx should be transformed to 324xxxxxxxx
        if (numberOnlyValue.startsWith("04") && numberOnlyValue.length === 10) {
          numberOnlyValue = numberOnlyValue.replace("04", "324");
        }

        // Connexys telephone number starts with 003 and has 13 digits (example: 0032496166716)
        return numberOnlyValue.padStart(13, "0");
      }),
    birthday: z
      .date({ error: t("common.error.birthday_format") })
      .transform((val) => {
        // Convert to 'YYYY-MM-DD'
        const formattedDate = formatToYYYYMMDD(val);
        // Convert 'YYYY-MM-DD' to '1904-MM-DD' because we don't ask for a real birt year, only for a day and month
        return `${1904}${formattedDate.slice(4)}`;
      }),
    postal_code: z.string().length(4, t("common.error.postal_code_format")),
    privacy_statement: z.boolean().refine((val) => val === true, {
      message: t("common.error.privacy_statement_required"),
    }),
    user_confirmed_potentially_invalid_email: z.boolean().optional(),
  });

  const spontaneousApplicationObject = z.object({
    office_key: z.string().min(1, t("common.error.required")),
    desired_function: z.string(),
  });

  const internalApplicationObject = z.object({
    desired_function: z.string(),
  });

  const studentApplicationObject = z.object({
    office_key: z.string().min(1, t("common.error.required")),
    desired_function: z.string(),
    type: z.literal("Student"),
  });

  const getSchema = () => {
    if (isStudent) {
      return z.intersection(baseSchemaObject, studentApplicationObject);
    }
    if (isSpontaneous) {
      if (isInternal) {
        return z.intersection(baseSchemaObject, internalApplicationObject);
      }
      return z.intersection(baseSchemaObject, spontaneousApplicationObject);
    }
    return baseSchemaObject;
  };

  const schema = getSchema();

  // If user is logged in, prefill the form
  const defaultValues = {
    first_name: candidate?.first_name ?? "",
    last_name: candidate?.name ?? "",
    email: candidate?.email ?? "",
    // in Connexys telephone number starts with 003 and has 13 digits
    telephone: candidate?.telephone
      ? candidate.telephone.replace("003", "+3")
      : "",
    birthday: candidate?.birth_date
      ? new Date(String(candidate.birth_date))
      : undefined,
    postal_code: candidate?.address?.postal_code ?? "",
    office_key: candidate?.office_key ?? "",
    privacy_statement: false,
    user_confirmed_potentially_invalid_email: false,
    ...(isStudent ? { type: "Student" } : {}),
    ...(officeKey ? { office_key: officeKey } : {}),
  };

  const form = useZodForm({
    schema: schema,
    defaultValues: defaultValues,
  });

  const isLoggedIn = !!session;

  async function createApplication(
    url: string,
    {
      arg,
    }: {
      arg: z.infer<typeof schema>;
    },
  ) {
    return await ofetch<CreateJobApplicationApiSuccessResponse>(url, {
      method: "POST",
      body: {
        job_id: job?.id,
        locale: locale,
        source: getUtmSourceFromSessionStorage(),
        ...arg,
        ...(isInternal ? { office_key: "HRM" } : {}),
      },
    });
  }

  const { trigger, error } = useSWRMutation("/api/apply", createApplication, {
    onError: (error) => {
      if (error?.data?.emailMayBeInvalid) {
        setIsEmailCorrectnessConfirmationModalOpen(true);
      }
    },
  });

  const handleSubmit = async (data: z.infer<typeof schema>) => {
    const response = await trigger(data);

    if (!response?.job_application_key) return;

    if (isSpontaneous) {
      const applyToAJobStep2Url = generateApplyToAJobStep2Url({
        applicationKey: response.job_application_key,
        candidateToken: response.candidate_token,
        jobId: undefined,
        locale: locale,
      });
      await router.replace(applyToAJobStep2Url);
    } else {
      createAppliedJobsCookie(job.id);

      const applyToAJobStep2Url = generateApplyToAJobStep2Url({
        applicationKey: response.job_application_key,
        candidateToken: response.candidate_token,
        jobId: job.id,
        locale: locale,
      });

      await router.replace(applyToAJobStep2Url);
    }
  };

  const handleConfirmEmailCorrectnessConfirmationModal = async () => {
    setIsEmailCorrectnessConfirmationModalOpen(false);
    form.setValue("user_confirmed_potentially_invalid_email", true);
    const submitForm = form.handleSubmit(handleSubmit);

    try {
      await submitForm();
    } catch {
      // do nothing
    }
  };

  const handleCancelEmailCorrectnessConfirmationModal = () => {
    setIsEmailCorrectnessConfirmationModalOpen(false);
    form.setValue("user_confirmed_potentially_invalid_email", true);
    // focus and scroll to the "email" field
    setTimeout(() => form.setFocus("email"), 500);
  };

  const inputProps = {
    className:
      "flex flex-col lg:grid lg:grid-cols-[250px_1fr] gap-2 lg:gap-8 items-stretch lg:items-center",
    // NOTE: if we disable these field for logged-in users, and fetching candidate info fails for whatever reason (or is incomplete). The user won't be apply to submit the form.
    // disabled: isLoggedIn,
    disabled: false,
    errorAfterInput: true,
    labelClasses: "!mb-0",
  };

  return (
    <>
      {isLoggedIn && !isSpontaneous && (
        <ConfirmApplicationModal
          job={job}
          locale={locale}
          open={isConfirmApplicationModalOpen}
          setOpen={setIsConfirmApplicationModalOpen}
        />
      )}

      <div className="relative">
        <h5 className="lg:mt-6">
          {t("pages.apply.application_form.subtitle")}
        </h5>
        <hr className="mb-8 mt-4" />

        <Form
          form={form}
          className="flex flex-col gap-6"
          onSubmit={handleSubmit}
          noValidate={true}
        >
          <input type="hidden" name="language" value={locale} />

          <TextInput
            label={t("common.form.first_name.label")}
            placeholder={t("common.form.first_name.placeholder")}
            name="first_name"
            autoComplete="given-name"
            {...inputProps}
            required
          />
          <TextInput
            label={t("common.form.last_name.label")}
            placeholder={t("common.form.last_name.placeholder")}
            name="last_name"
            autoComplete="family-name"
            {...inputProps}
            required
          />
          <TextInput
            label={t("common.form.email.label")}
            placeholder={t("common.form.email.placeholder")}
            name="email"
            type="email"
            autoComplete="email"
            {...inputProps}
            required
          />
          <TextInput
            label={t("common.form.phone.label")}
            placeholder={t("common.form.phone.placeholder")}
            name="telephone"
            type="tel"
            autoComplete="tel"
            {...inputProps}
            required
          />
          <BirthDayInput
            label={t("common.form.birthday.label")}
            placeholder={t("common.form.birthday.placeholder")}
            name="birthday"
            locale={locale}
            initialValue={form.watch("birthday")}
            {...inputProps}
            required
          />
          <PostalCodeInput
            label={t("common.form.postal_code.label")}
            placeholder={t("common.form.postal_code.placeholder")}
            inputName="postal_code"
            locale={locale}
            i18n={{
              no_results: t(
                "pages.apply.application_form.postal_code.no_results",
              ),
            }}
            {...inputProps}
            required
          />

          {(isSpontaneous || isStudent) && (
            <>
              <div className="mt-6">
                <h5>
                  {t("pages.apply.application_form.application_subtitle")}
                </h5>
                <hr className="mt-4" />
              </div>

              <TextInput
                label={t("pages.apply.application_form.desired_function.label")}
                placeholder={t(
                  "pages.apply.application_form.desired_function.placeholder",
                )}
                name="desired_function"
                className={inputProps.className}
                labelClasses={inputProps.labelClasses}
              />
              {!isInternal && (
                <OfficeSelector
                  label={t("common.form.office.label")}
                  placeholder={t("common.form.office.placeholder")}
                  name="office_key"
                  locale={locale}
                  className={cn(inputProps.className, "lg:items-start")}
                  labelClasses={cn(inputProps.labelClasses, "mt-1")}
                  required
                />
              )}
            </>
          )}

          <Checkbox
            label={t("pages.apply.application_form.privacy_statement.label")}
            name="privacy_statement"
            value="true"
            className="mt-6"
            asBoolean
            checked={form.watch("privacy_statement")}
          />

          {error && !error?.data?.emailMayBeInvalid && (
            <div className="form-error text-lg">
              {t("pages.apply.application_form.error.message")}
            </div>
          )}

          <div className="mt-6 flex justify-center border-t pt-8">
            <Button
              type="submit"
              className="btn-lg"
              disabled={form.formState.isSubmitting}
              hasLoader={true}
              isLoading={form.formState.isSubmitting}
            >
              {t("pages.apply.application_form.submit_button")}
            </Button>
          </div>
        </Form>

        {/*<Loader*/}
        {/*  className="absolute inset-0 animate-loaderFadeIn bg-white"*/}
        {/*  size="large"*/}
        {/*  showMessages={true}*/}
        {/*  messages={[*/}
        {/*    t("pages.apply.application_form.applied.loading_message_1"),*/}
        {/*    t("pages.apply.application_form.applied.loading_message_2"),*/}
        {/*    t("pages.apply.application_form.applied.loading_message_3"),*/}
        {/*    t("pages.apply.application_form.applied.loading_message_4"),*/}
        {/*    t("pages.apply.application_form.applied.loading_message_5"),*/}
        {/*  ]}*/}
        {/*  locale={locale}*/}
        {/*/>*/}

        {form.formState.isSubmitting && (
          <Loader
            className="absolute inset-0 animate-loaderFadeIn bg-white"
            size="large"
            showMessages={true}
            messages={[
              t("pages.apply.application_form.applied.loading_message_1"),
              t("pages.apply.application_form.applied.loading_message_2"),
              t("pages.apply.application_form.applied.loading_message_3"),
              t("pages.apply.application_form.applied.loading_message_4"),
              t("pages.apply.application_form.applied.loading_message_5"),
            ]}
            locale={locale}
          />
        )}
      </div>

      <EmailCorrectnessConfirmationModal
        email={form.watch("email")}
        locale={locale}
        open={isEmailCorrectnessConfirmationModalOpen}
        onCancel={handleCancelEmailCorrectnessConfirmationModal}
        onConfirm={handleConfirmEmailCorrectnessConfirmationModal}
      />
    </>
  );
};
